import { EventEmitter } from 'eventemitter3';
/**
 * 执行一个函数，并返回结果（相对优雅的立即执行函数）
 * @param fn 要执行的函数
 * @returns 结果
 */
export function invoke<T>(fn: () => T) {
  return fn();
}

export const isBrowser = typeof window !== 'undefined';

export const memoize = <T extends (...args: any[]) => any>(fn: T) => {
  const cache = new Map<string, T>();
  return (...args: Parameters<T>) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key) as ReturnType<T>;
    }
    const result = fn(...args);
    if (result != undefined) {
      cache.set(key, result);
      if (result.then && result.catch) {
        result.catch((err) => {
          cache.delete(key);
        });
      }
    }
    return result as ReturnType<T>;
  };
};

export const randomPick = <T>(arr: T[]) => {
  return arr[Math.floor(Math.random() * arr.length)];
};

export const addListener = <T extends EventEmitter.ValidEventTypes, Event extends EventEmitter.EventNames<T>>(
  eventBus: EventEmitter<T>,
  event: Event,
  fn: EventEmitter.EventListener<T, Event>,
) => {
  eventBus.on(event, fn);
  return () => {
    eventBus.off?.(event, fn);
  };
};

type CacheObject = {
  value: unknown;
  expireTime: number;
  id?: string;
};

// 默认缓存时间一个月
export const cacheObj = (
  value: unknown,
  key?: string,
  {
    expire = 1000 * 60 * 60 * 24 * 30,
    id,
  }: {
    expire?: number;
    id?: string;
  } = {},
) => {
  if (!key || !isBrowser) {
    return;
  }
  if (value === undefined || value === null) {
    localStorage.removeItem(key);
    return;
  }
  const cache: CacheObject = {
    value,
    expireTime: Date.now() + expire,
  };
  if (id) {
    cache.id = id;
  }
  localStorage.setItem(key, JSON.stringify(cache));
};

export const getCacheObj = <T>(key?: string, id?: string) => {
  if (!key || !isBrowser) {
    return;
  }
  const cacheStr = localStorage.getItem(key);
  if (!cacheStr || !isBrowser) {
    return;
  }
  try {
    const cache = JSON.parse(cacheStr) as CacheObject;
    if (cache.expireTime < Date.now() || (id && cache.id !== id)) {
      localStorage.removeItem(key);
      return;
    }
    return cache.value as T;
  } catch (e) {
    return;
  }
};

export type ValueOrPromise<T> = Promise<T> | T;

export const hexNumberString = (str: string | number) => {
  const num = Number(str);
  if (Number.isNaN(num)) {
    return '';
  }
  return `0x${num.toString(16)}`;
};

export const isSameChain = (chain1?: string | number, chain2?: string | number) => {
  return Number(chain1) === Number(chain2);
};

export type AnyFunc = (...args: any[]) => any;

export const onlyRunOnce = <T extends AnyFunc>(fn: T) => {
  let called = false;
  let rs: ReturnType<T>;
  return (...args: Parameters<T>) => {
    if (called) {
      return rs;
    }
    rs = fn(...args);
    called = true;
    return rs;
  };
};

export const setProps = <T>(defaults: T, conf: Partial<T>) => {
  Object.entries(conf).forEach(([key, value]) => {
    if (value != undefined) {
      defaults[key] = value;
    }
  });
  return defaults;
};

/**
 * 简单判断是不是一个合法 evm地址，（不做hash校验）
 * @param address 地址
 * @param require0x 0x 必须？
 * @returns
 */
export const isValidEVMAddress = (address?: string, require0x = true): boolean => {
  // 检查是否是字符串
  if (!address) {
    return false;
  }
  // 检查是否只包含十六进制字符
  const hexRegex = new RegExp(`^(0x)${require0x ? '' : '?'}[0-9a-fA-F]{40}$`);
  return hexRegex.test(address);
};
